import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Heart, Sparkle, Download, Boxes } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

// Retour de Stripe après un paiement : abonnement (semaine offerte) ou achat boutique
// (e-book → téléchargement du PDF, programme → débloqué à vie dans la boutique).
export default function PaiementSucces() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [purchase, setPurchase] = useState(null); // { item_kind, item_id, item_title }
  const [giftCode, setGiftCode] = useState(null); // carte cadeau : code délivré après paiement

  // Abonnement créé dans le parcours Mon Instant : l'Instant composé l'attend après le paiement
  let pendingInstant = null;
  try {
    pendingInstant = JSON.parse(localStorage.getItem("rav-instant") || "null");
  } catch {
    /* ignore */
  }
  const hasPendingInstant = !!pendingInstant?.minutes;

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (data.item_kind) setPurchase({ kind: data.item_kind, id: data.item_id, title: data.item_title });
          if (data.gift_code) setGiftCode(data.gift_code);
          setState("paid");
          return;
        }
      } catch {
        /* on réessaie */
      }
      tries += 1;
      if (tries < 15) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [searchParams]);

  // Téléchargement du PDF acheté (endpoint protégé : achat vérifié côté serveur)
  const downloadEbook = async () => {
    const token = localStorage.getItem("rav-token");
    try {
      const res = await axios.get(`${API}/ebooks/${purchase.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${purchase.title || "ebook"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Téléchargement impossible pour le moment — retrouvez votre e-book dans la Boutique.");
    }
  };
  return (
    <main className="rav-page" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
        <img src="/logo-accueil.webp" alt="Respire Aime Vibre" className="rav-page-logo" />
        {state === "checking" && (
          <>
            <h1 className="rav-page-title">Nous vérifions votre paiement…</h1>
            <p className="rav-page-sub" data-testid="payment-checking">Quelques secondes suffisent.</p>
          </>
        )}
        {state === "paid" && purchase?.kind === "ebook" && (
          <>
            <Heart size={30} strokeWidth={1.4} color="#9A6933" />
            <h1 className="rav-page-title" data-testid="payment-success-title">« {purchase.title} » est à vous</h1>
            <p className="rav-page-sub" data-testid="payment-success-sub">
              Merci pour votre confiance. Votre e-book vous attend — téléchargez-le, il restera disponible dans la Boutique.
            </p>
            <button type="button" className="rav-primary-button" data-testid="payment-download-ebook" onClick={downloadEbook}>
              <Download size={16} strokeWidth={1.6} />
              Télécharger mon PDF
            </button>
            <button type="button" className="rav-text-link" data-testid="payment-success-boutique" onClick={() => navigate("/boutique?tab=ebooks")}>
              Retour à la Boutique
            </button>
          </>
        )}
        {state === "paid" && purchase?.kind === "program" && (
          <>
            <Heart size={30} strokeWidth={1.4} color="#9A6933" />
            <h1 className="rav-page-title" data-testid="payment-success-title">« {purchase.title} » est débloqué</h1>
            <p className="rav-page-sub" data-testid="payment-success-sub">
              Votre programme est à vous, pour toujours. Installez-vous doucement, tout est prêt.
            </p>
            <button type="button" className="rav-primary-button" data-testid="payment-open-program" onClick={() => navigate("/boutique?tab=programmes")}>
              <Boxes size={16} strokeWidth={1.6} />
              Ouvrir mon programme
            </button>
          </>
        )}
        {state === "paid" && giftCode && (
          <div className="rav-gift-card" data-testid="payment-gift-card">
            <p className="rav-page-sub">Votre carte cadeau est prête 🤎 Offrez ce code :</p>
            <p className="rav-gift-code" data-testid="payment-gift-code">{giftCode}</p>
            <p className="rav-page-sub">
              Il donne droit à 30 jours d'Instants illimités. Il vient aussi d'être envoyé par e-mail. Pour l'activer : Profil → « J'ai une carte cadeau ».
            </p>
          </div>
        )}
        {state === "paid" && !purchase && (
          <>
            <Heart size={30} strokeWidth={1.4} color="#9A6933" />
            <h1 className="rav-page-title" data-testid="payment-success-title">Bienvenue dans Respire Aime Vibre +</h1>
            <p className="rav-page-sub" data-testid="payment-success-sub">
              Votre abonnement est actif : tous vos Instants sont désormais illimités.
              Sans engagement — vous pouvez résilier à tout moment depuis votre Profil.
            </p>
            <button type="button" className="rav-primary-button" data-testid="payment-success-home" onClick={() => navigate(hasPendingInstant ? "/instant" : "/accueil")}>
              <Sparkle size={16} strokeWidth={1.6} />
              {hasPendingInstant ? "Retrouver mon Instant" : "Découvrir mes Instants illimités"}
            </button>
          </>
        )}
        {state === "pending" && (
          <>
            <h1 className="rav-page-title" data-testid="payment-pending-title">Tout est en bonne voie</h1>
            <p className="rav-page-sub">
              La confirmation de votre banque prend un peu plus de temps que prévu — vos Instants illimités
              s'activeront automatiquement dès sa réception.
            </p>
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="payment-pending-home" onClick={() => navigate("/accueil")}>
              Retour à l'accueil
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="rav-page-title">Une vérification est nécessaire</h1>
            <p className="rav-page-sub">Écrivez-nous depuis la page Échanger si le moindre doute subsiste.</p>
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="payment-error-home" onClick={() => navigate("/accueil")}>
              Retour à l'accueil
            </button>
          </>
        )}
      </motion.div>
    </main>
  );
}
